/**
 * Converts event date information to a JavaScript Date object
 * @param {Object} event - Event object with month, date and year properties
 * @returns {Date} JavaScript Date object
 */
export const getEventDate = (event) => {
  // Map month abbreviations to numbers
  const monthMap = {
    JAN: 0,
    FEB: 1,
    MAR: 2,
    APR: 3,
    MAY: 4,
    JUN: 5,
    JUL: 6,
    AUG: 7,
    SEP: 8,
    OCT: 9,
    NOV: 10,
    DEC: 11,
  };

  // Create date object for the event using the year property
  return new Date(event.year, monthMap[event.month], parseInt(event.day));
};

/**
 * Filters and sorts events to get the closest upcoming events
 * @param {Array} events - Array of event objects
 * @param {number} limit - Maximum number of events to return
 * @returns {Array} Filtered and sorted array of events
 */
export const getUpcomingEvents = (events, limit = 3) => {
  const currentDate = new Date();

  // Filter out past events
  const upcomingEvents = events.filter((event) => {
    const eventDate = getEventDate(event);
    return eventDate >= currentDate;
  });

  // Sort events by date (closest first)
  const sortedEvents = [...upcomingEvents].sort((a, b) => {
    const dateA = getEventDate(a);
    const dateB = getEventDate(b);
    return dateA - dateB;
  });

  // Get the closest events up to the specified limit
  return sortedEvents.slice(0, limit);
};

export const isEventPast = (event) => {
  const currentDate = new Date();
  const eventDate = getEventDate(event);

  // If the event has time information, incorporate it
  if (event.time) {
    // Parse time (assuming format like "7:00 PM")
    const timeParts = event.time.match(/(\d+):(\d+)\s?(AM|PM)?/i);

    if (timeParts) {
      let hours = parseInt(timeParts[1]);
      const minutes = parseInt(timeParts[2]);
      const ampm = timeParts[3]?.toUpperCase();

      // Convert to 24-hour format if AM/PM is specified
      if (ampm) {
        if (ampm === "PM" && hours < 12) {
          hours += 12;
        } else if (ampm === "AM" && hours === 12) {
          hours = 0;
        }
      }

      // Set the time components
      eventDate.setHours(hours, minutes, 0, 0);
    }
  } else {
    // If no time specified, default to end of day (23:59:59)
    eventDate.setHours(23, 59, 59, 999);
  }

  // Compare with current date/time
  return eventDate < currentDate;
};
